import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Eye, EyeOff, Lock, Mail, User } from 'lucide-react';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useForm } from '../../hooks/useForm';
import { validateEmail, validatePassword, validateUsername } from '../../utils/helpers';

const SignupContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gray50} 0%, ${({ theme }) => theme.colors.gray100} 100%);
  padding: ${({ theme }) => theme.spacing.md};
`;

const SignupCard = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  width: 100%;
  max-width: 400px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};
`;

const LogoIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  color: ${({ theme }) => theme.colors.white};
`;

const LogoText = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const LogoSubtext = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0 0 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.gray400};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing['2xl']};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray400};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  
  &:hover {
    color: ${({ theme }) => theme.colors.gray600};
    background-color: ${({ theme }) => theme.colors.gray100};
  }
`;

const PasswordStrength = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const StrengthBar = styled.div`
  height: 4px;
  background-color: ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StrengthFill = styled.div`
  height: 100%;
  width: ${({ $strength }) => $strength}%;
  background-color: ${({ $strength, theme }) => {
    if ($strength < 25) return theme.colors.error;
    if ($strength < 50) return theme.colors.warning;
    if ($strength < 75) return theme.colors.info;
    return theme.colors.success;
  }};
  transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out;
`;

const StrengthText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ $strength, theme }) => {
    if ($strength < 25) return theme.colors.error;
    if ($strength < 50) return theme.colors.warning;
    if ($strength < 75) return theme.colors.info;
    return theme.colors.success;
  }};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const TermsText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0;
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const validationRules = {
  email: [
    (value) => !value ? 'Email is required' : '',
    (value) => !validateEmail(value) ? 'Please enter a valid email' : '',
  ],
  username: [
    (value) => !value ? 'Username is required' : '',
    (value) => !validateUsername(value) ? 'Username must be 3-50 characters' : '',
  ],
  password: [
    (value) => !value ? 'Password is required' : '',
    (value) => !validatePassword(value) ? 'Password must be at least 8 characters' : '',
  ],
  confirmPassword: [
    (value, allValues) => !value ? 'Please confirm your password' : '',
    (value, allValues) => value !== allValues.password ? 'Passwords do not match' : '',
  ],
};

const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 25;
  return strength;
};

const getStrengthLabel = (strength) => {
  if (strength < 25) return 'Weak';
  if (strength < 50) return 'Fair';
  if (strength < 75) return 'Good';
  return 'Strong';
};

function SignupPage() {
  const { signup, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validateForm,
  } = useForm({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  }, validationRules);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const result = await signup(values.email, values.username, values.password);
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const passwordStrength = getPasswordStrength(values.password);

  return (
    <SignupContainer>
      <SignupCard>
        <Logo>
          <LogoIcon>🔐</LogoIcon>
          <LogoText>Bunkr</LogoText>
          <LogoSubtext>Create your secure vault</LogoSubtext>
        </Logo>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <InputIcon>
              <Mail size={18} />
            </InputIcon>
            <StyledInput
              type="email"
              name="email"
              placeholder="Enter your email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.email}
              required
            />
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <User size={18} />
            </InputIcon>
            <StyledInput
              type="text"
              name="username"
              placeholder="Choose a username"
              value={values.username}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.username}
              required
            />
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Lock size={18} />
            </InputIcon>
            <StyledInput
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Create a password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.password}
              required
            />
            <PasswordToggle
              type="button"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </PasswordToggle>
            {values.password && (
              <PasswordStrength>
                <StrengthBar>
                  <StrengthFill $strength={passwordStrength} />
                </StrengthBar>
                <StrengthText $strength={passwordStrength}>
                  {getStrengthLabel(passwordStrength)}
                </StrengthText>
              </PasswordStrength>
            )}
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Lock size={18} />
            </InputIcon>
            <StyledInput
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm your password"
              value={values.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.confirmPassword}
              required
            />
            <PasswordToggle
              type="button"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </PasswordToggle>
          </InputGroup>

          <TermsText>
            By signing up, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </TermsText>

          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={loading}
          >
            Create Account
          </Button>
        </Form>

        <LoginLink>
          Already have an account?{' '}
          <Link to="/login">Sign in</Link>
        </LoginLink>
      </SignupCard>
    </SignupContainer>
  );
}

export default SignupPage;
